import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Percent, Filter, Info } from 'lucide-react';
import { PackProbabilityStats, ProbabilityFilterOptions } from '../../types/pokemon';
import { SUPABASE_IMAGE_BASE_URL } from '../../lib/constants';
import { useI18n } from '../../contexts/I18nContext';

interface PackProbabilitiesProps {
  packStats: PackProbabilityStats[];
  filterOptions?: ProbabilityFilterOptions;
  onFilterChange?: (options: ProbabilityFilterOptions) => void;
}

// Ordre et style des raretés
const rarityConfig: Record<string, { icon: string, color: string }> = {
  '♛': { icon: '♛', color: 'text-yellow-600' },
  '☆☆☆': { icon: '★★★', color: 'text-purple-600' },
  '☆☆': { icon: '★★', color: 'text-blue-600' },
  '☆': { icon: '★', color: 'text-indigo-600' },
  '♢♢♢♢': { icon: '♦♦♦♦', color: 'text-emerald-600' },
  '♢♢♢': { icon: '♦♦♦', color: 'text-green-600' },
  '♢♢': { icon: '♦♦', color: 'text-teal-600' },
  '♢': { icon: '♦', color: 'text-cyan-600' },
  '✧': { icon: '✧', color: 'text-amber-500' },    // Shiny (1 icône)
  '✧✧': { icon: '✧✧', color: 'text-amber-600' }   // Shiny (2 icônes)
};

const RarityCollection = ({ rarityStats }: { rarityStats: Record<string, { total: number, owned: number, missing: number, lessThanTwo: number, tradable: number }> }) => {
  if (Object.keys(rarityStats).length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-3 mt-2 text-xs">
      {Object.entries(rarityStats)
        .sort(([a], [b]) => {
          const orderA = Object.keys(rarityConfig).indexOf(a);
          const orderB = Object.keys(rarityConfig).indexOf(b);
          return orderA - orderB;
        })
        .map(([rarity, stats]) => {
          const config = rarityConfig[rarity] || { icon: rarity, color: 'text-gray-600' };
          return (
            <div 
              key={rarity}
              className="flex items-center gap-1"
              title={`${stats.owned}/${stats.total} cartes de rareté ${rarity}`}
            >
              <span className={`font-medium ${config.color}`}>{config.icon}</span>
              <span className="text-gray-500">{stats.owned}/{stats.total}</span>
            </div>
          );
        })}
    </div>
  );
};

// Formater un pourcentage avec 2 décimales
const formatPercent = (value: number) => {
  // S'assurer que la valeur est entre 0 et 1
  const clampedValue = Math.max(0, Math.min(1, value));
  
  // Convertir en pourcentage avec 2 décimales
  const percentage = (clampedValue * 100).toFixed(2);
  
  // Supprimer les zéros inutiles à la fin (ex: 25.00% -> 25%)
  const formattedPercentage = parseFloat(percentage).toString();
  
  return formattedPercentage + '%';
};

export function PackProbabilities({ 
  packStats, 
  filterOptions = { showMissing: true, showLessThanTwo: false, excludeTradable: false },
  onFilterChange
}: PackProbabilitiesProps) {
  const { t } = useI18n();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSectionExpanded, setIsSectionExpanded] = useState(true);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showProbabilityInfo, setShowProbabilityInfo] = useState(false);

  if (!packStats?.length) return null;

  const bestPack = packStats[0];
  const otherPacks = packStats.slice(1);

  return (
    <div className="space-y-4">
      {/* En-tête avec bouton de minimisation */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Percent className="w-5 h-5 text-indigo-600" />
          <h2 className="text-lg font-semibold text-gray-900">{t('pack_probabilities.title')}</h2>
        </div>
        <button 
          onClick={() => setIsSectionExpanded(!isSectionExpanded)}
          className="sm:hidden flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 text-gray-500"
        >
          {isSectionExpanded ? (
            <ChevronUp className="w-5 h-5" />
          ) : (
            <ChevronDown className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Contenu principal */}
      {isSectionExpanded && (
        <div>
          {/* Bouton pour afficher/masquer les informations sur les probabilités */}
          <div className="mb-4">
            <div 
              className="bg-gradient-to-r from-indigo-500 to-purple-600 px-4 py-3 rounded-lg shadow-sm cursor-pointer"
              onClick={() => setShowProbabilityInfo(!showProbabilityInfo)}
            >
              <h3 className="text-sm font-semibold text-white flex items-center justify-between">
                <div className="flex items-center">
                  <Info className="w-4 h-4 mr-2" />
                  {t('sets.boosters.probabilities_title')}
                </div>
                {showProbabilityInfo ? (
                  <ChevronUp className="w-4 h-4 text-white" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-white" />
                )}
              </h3>
            </div>
            
            {showProbabilityInfo && (
              <div className="mt-3 p-4 sm:p-5 bg-white rounded-lg border border-indigo-100 shadow-sm">
                <h4 className="text-sm sm:text-base font-semibold text-indigo-800 mb-3">{t('sets.boosters.position_rarity')}</h4>
                <p className="text-xs sm:text-sm text-gray-600 mb-4">{t('sets.boosters.booster_content')}</p>
                
                <div className="space-y-5">
                  {/* Positions 1-3 */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-3 rounded-lg border border-blue-100">
                    <h5 className="text-xs sm:text-sm font-semibold text-indigo-700 mb-2 flex items-center">
                      <span className="inline-flex items-center justify-center bg-indigo-100 text-indigo-800 w-5 h-5 sm:w-6 sm:h-6 rounded-full mr-2 font-bold text-xs sm:text-sm">1-3</span>
                      {t('sets.boosters.positions_1_3')}
                    </h5>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-white p-2 rounded-md shadow-sm flex items-center justify-between">
                        <div className="text-xs sm:text-sm font-medium text-gray-700">♢</div>
                        <div className="font-semibold text-xs sm:text-sm text-indigo-600">100%</div>
                      </div>
                      <div className="bg-white p-2 rounded-md shadow-sm flex items-center justify-between">
                        <div className="text-xs sm:text-sm font-medium text-gray-700">{t('sets.boosters.others')}</div>
                        <div className="font-semibold text-xs sm:text-sm text-indigo-600">0%</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Position 4 */}
                  <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-3 rounded-lg border border-purple-100">
                    <h5 className="text-xs sm:text-sm font-semibold text-indigo-700 mb-2 flex items-center">
                      <span className="inline-flex items-center justify-center bg-indigo-100 text-indigo-800 w-5 h-5 sm:w-6 sm:h-6 rounded-full mr-2 font-bold text-xs sm:text-sm">4</span>
                      {t('sets.boosters.position_4')}
                    </h5>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between bg-white p-2 rounded-md shadow-sm">
                        <div className="text-xs sm:text-sm font-medium text-gray-700">♢♢</div>
                        <div className="font-semibold text-xs sm:text-sm text-indigo-600">90%</div>
                      </div>
                      <div className="flex items-center justify-between bg-white p-2 rounded-md shadow-sm">
                        <div className="text-xs sm:text-sm font-medium text-gray-700">♢♢♢</div>
                        <div className="font-semibold text-xs sm:text-sm text-indigo-600">5%</div>
                      </div>
                      <div className="flex items-center justify-between bg-white p-2 rounded-md shadow-sm">
                        <div className="text-xs sm:text-sm font-medium text-gray-700">♢♢♢♢</div>
                        <div className="font-semibold text-xs sm:text-sm text-indigo-600">1.67%</div>
                      </div>
                      <div className="flex items-center justify-between bg-white p-2 rounded-md shadow-sm">
                        <div className="text-xs sm:text-sm font-medium text-gray-700">★</div>
                        <div className="font-semibold text-xs sm:text-sm text-indigo-600">2.57%</div>
                      </div>
                      <div className="flex items-center justify-between bg-white p-2 rounded-md shadow-sm">
                        <div className="text-xs sm:text-sm font-medium text-gray-700">★★</div>
                        <div className="font-semibold text-xs sm:text-sm text-indigo-600">0.5%</div>
                      </div>
                      <div className="flex items-center justify-between bg-white p-2 rounded-md shadow-sm">
                        <div className="text-xs sm:text-sm font-medium text-gray-700">★★★</div>
                        <div className="font-semibold text-xs sm:text-sm text-indigo-600">0.22%</div>
                      </div>
                      <div className="flex items-center justify-between bg-white p-2 rounded-md shadow-sm">
                        <div className="text-xs sm:text-sm font-medium text-gray-700">♛</div>
                        <div className="font-semibold text-xs sm:text-sm text-indigo-600">0.04%</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Position 5 */}
                  <div className="bg-gradient-to-r from-indigo-50 to-violet-50 p-3 rounded-lg border border-indigo-100">
                    <h5 className="text-xs sm:text-sm font-semibold text-indigo-700 mb-2 flex items-center">
                      <span className="inline-flex items-center justify-center bg-indigo-100 text-indigo-800 w-5 h-5 sm:w-6 sm:h-6 rounded-full mr-2 font-bold text-xs sm:text-sm">5</span>
                      {t('sets.boosters.position_5')}
                    </h5>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between bg-white p-2 rounded-md shadow-sm">
                        <div className="text-xs sm:text-sm font-medium text-gray-700">♢♢</div>
                        <div className="font-semibold text-xs sm:text-sm text-indigo-600">60%</div>
                      </div>
                      <div className="flex items-center justify-between bg-white p-2 rounded-md shadow-sm">
                        <div className="text-xs sm:text-sm font-medium text-gray-700">♢♢♢</div>
                        <div className="font-semibold text-xs sm:text-sm text-indigo-600">20%</div>
                      </div>
                      <div className="flex items-center justify-between bg-white p-2 rounded-md shadow-sm">
                        <div className="text-xs sm:text-sm font-medium text-gray-700">♢♢♢♢</div>
                        <div className="font-semibold text-xs sm:text-sm text-indigo-600">6.66%</div>
                      </div>
                      <div className="flex items-center justify-between bg-white p-2 rounded-md shadow-sm">
                        <div className="text-xs sm:text-sm font-medium text-gray-700">★</div>
                        <div className="font-semibold text-xs sm:text-sm text-indigo-600">10.29%</div>
                      </div>
                      <div className="flex items-center justify-between bg-white p-2 rounded-md shadow-sm">
                        <div className="text-xs sm:text-sm font-medium text-gray-700">★★</div>
                        <div className="font-semibold text-xs sm:text-sm text-indigo-600">2%</div>
                      </div>
                      <div className="flex items-center justify-between bg-white p-2 rounded-md shadow-sm">
                        <div className="text-xs sm:text-sm font-medium text-gray-700">★★★</div>
                        <div className="font-semibold text-xs sm:text-sm text-indigo-600">0.89%</div>
                      </div>
                      <div className="flex items-center justify-between bg-white p-2 rounded-md shadow-sm">
                        <div className="text-xs sm:text-sm font-medium text-gray-700">♛</div>
                        <div className="font-semibold text-xs sm:text-sm text-indigo-600">0.16%</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Filtres avancés */}
          {onFilterChange && (
            <div className="mb-4">
              <div 
                className="bg-gradient-to-r from-indigo-500 to-purple-600 px-4 py-3 rounded-lg shadow-sm cursor-pointer"
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              >
                <h3 className="text-sm font-semibold text-white flex items-center justify-between">
                  <div className="flex items-center">
                    <Filter className="w-4 h-4 mr-2" />
                    {t('sets.boosters.advanced_filters')}
                  </div>
                  {showAdvancedFilters ? (
                    <ChevronUp className="w-4 h-4 text-white" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-white" />
                  )}
                </h3>
              </div>
              
              {showAdvancedFilters && (
                <div className="mt-3 p-4 sm:p-5 bg-white rounded-lg border border-indigo-100 shadow-sm">
                  <h4 className="text-sm sm:text-base font-semibold text-indigo-800 mb-4">{t('sets.boosters.card_types')}</h4>
                  
                  <div className="space-y-3">
                    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                      <div className="p-3 border-b border-gray-100 bg-gray-50">
                        <h5 className="text-xs sm:text-sm font-medium text-gray-700">{t('sets.boosters.search_criteria')}</h5>
                      </div>
                      <div className="p-4">
                        <div className="space-y-3">
                          <div className="flex items-center">
                            <input
                              type="radio"
                              id="showMissingOnly"
                              name="cardFilter"
                              checked={filterOptions?.showMissing && !filterOptions?.showLessThanTwo}
                              onChange={() => onFilterChange({
                                ...filterOptions,
                                showMissing: true,
                                showLessThanTwo: false
                              })}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                            />
                            <label htmlFor="showMissingOnly" className="ml-2 block text-sm font-medium text-gray-700">
                              {t('sets.boosters.missing_only')}
                            </label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="radio"
                              id="showLessThanTwo"
                              name="cardFilter"
                              checked={filterOptions?.showLessThanTwo}
                              onChange={() => onFilterChange({
                                ...filterOptions,
                                showMissing: false,
                                showLessThanTwo: true
                              })}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                            />
                            <label htmlFor="showLessThanTwo" className="ml-2 block text-sm font-medium text-gray-700">
                              {t('sets.boosters.missing_or_incomplete')}
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                      <div className="p-3 border-b border-gray-100 bg-gray-50">
                        <h5 className="text-xs sm:text-sm font-medium text-gray-700">{t('sets.boosters.additional_options')}</h5>
                      </div>
                      <div className="p-4">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="excludeTradable"
                            checked={filterOptions?.excludeTradable}
                            onChange={() => onFilterChange({
                              ...filterOptions,
                              excludeTradable: !filterOptions.excludeTradable
                            })}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                          <label htmlFor="excludeTradable" className="ml-2 block text-sm font-medium text-gray-700">
                            {t('sets.boosters.exclude_tradable')}
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Pack recommandé */}
          <div className="bg-white rounded-lg p-3 sm:p-4 shadow border border-gray-100">
            <div className="mb-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-500">
                  {bestPack.packImage ? (
                    <div className="flex items-center gap-2">
                      <img
                        src={`${SUPABASE_IMAGE_BASE_URL}${bestPack.packImage}`}
                        alt={bestPack.pack}
                        className="w-6 h-6 object-contain"
                      />
                      <span>{bestPack.pack}</span>
                    </div>
                  ) : (
                    bestPack.pack
                  )}
                </h3>
                <div className="flex items-center gap-2">
                  <span className="text-xs px-2 py-1 bg-indigo-50 text-indigo-700 rounded-full font-medium">
                    {formatPercent(bestPack.global)}
                  </span>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-3 gap-2">
                <div className="bg-blue-50 p-2 rounded-lg">
                  <div className="text-xs text-blue-800 font-medium mb-1">{t('sets.boosters.positions_explanation.stats_1_3')}</div>
                  <div className="text-blue-900 font-semibold">{formatPercent(bestPack.firstThreeCards)}</div>
                </div>
                <div className="bg-purple-50 p-2 rounded-lg">
                  <div className="text-xs text-purple-800 font-medium mb-1">{t('sets.boosters.positions_explanation.position_4')}</div>
                  <div className="text-purple-900 font-semibold">{formatPercent(bestPack.fourthCard)}</div>
                </div>
                <div className="bg-indigo-50 p-2 rounded-lg">
                  <div className="text-xs text-indigo-800 font-medium mb-1">{t('sets.boosters.positions_explanation.position_5')}</div>
                  <div className="text-indigo-900 font-semibold">{formatPercent(bestPack.fifthCard)}</div>
                </div>
              </div>

              <div className="mt-4">
                <RarityCollection rarityStats={bestPack.rarityStats} />
              </div>
            </div>
          </div>

          {/* Autres packs */}
          {otherPacks.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-500">{t('sets.boosters.other_packs')}</h3>
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="text-xs font-medium px-2 py-1 text-gray-500 hover:text-gray-700 flex items-center gap-1"
                >
                  {isExpanded ? (
                    <>
                      {t('sets.boosters.hide')}
                      <ChevronUp className="w-3 h-3" />
                    </>
                  ) : (
                    <>
                      {t('sets.boosters.view')} {otherPacks.length} {t('sets.boosters.packs')}
                      <ChevronDown className="w-3 h-3" />
                    </>
                  )}
                </button>
              </div>

              {isExpanded && (
                <div className="mt-3 space-y-3">
                  {otherPacks.map((pack, index) => (
                    <div key={index} className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-medium text-gray-500">
                          {pack.packImage ? (
                            <div className="flex items-center gap-2">
                              <img
                                src={`${SUPABASE_IMAGE_BASE_URL}${pack.packImage}`}
                                alt={pack.pack}
                                className="w-6 h-6 object-contain"
                              />
                              <span>{pack.pack}</span>
                            </div>
                          ) : (
                            pack.pack
                          )}
                        </h3>
                        <div className="flex items-center gap-2">
                          <span className="text-xs px-2 py-1 bg-indigo-50 text-indigo-700 rounded-full font-medium">
                            {formatPercent(pack.global)}
                          </span>
                        </div>
                      </div>

                      <div className="mt-3 grid grid-cols-3 gap-2">
                        <div className="bg-blue-50 p-2 rounded-lg">
                          <div className="text-xs text-blue-800 font-medium mb-1">{t('sets.boosters.positions_explanation.new_cards_1_3')}</div>
                          <div className="text-blue-900 font-semibold">{formatPercent(pack.firstThreeCards)}</div>
                        </div>
                        <div className="bg-purple-50 p-2 rounded-lg">
                          <div className="text-xs text-purple-800 font-medium mb-1">{t('sets.boosters.positions_explanation.new_card_4')}</div>
                          <div className="text-purple-900 font-semibold">{formatPercent(pack.fourthCard)}</div>
                        </div>
                        <div className="bg-indigo-50 p-2 rounded-lg">
                          <div className="text-xs text-indigo-800 font-medium mb-1">{t('sets.boosters.positions_explanation.new_card_5')}</div>
                          <div className="text-indigo-900 font-semibold">{formatPercent(pack.fifthCard)}</div>
                        </div>
                      </div>

                      <div className="mt-4">
                        <RarityCollection rarityStats={pack.rarityStats} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
} 